import type { FC, MouseEvent, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PinIcon from '@mui/icons-material/Pin';
import { Label, MenuPopover } from '@pflegenavi/web-components';
import { MenuBarIconButton } from '@pflegenavi/frontend/layout';
import { useTranslation } from 'react-i18next';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { EmployeeCardWithDates } from '@pflegenavi/frontend/api-nursing-home';
import {
  canShowCardPin,
  getContextualLimitLabel,
  getLimitUsage,
  getSpentAmount,
  getNextResetDate,
  getStatusColor,
  isCardTerminated,
} from '@pflegenavi/frontend/api-nursing-home';
import { EmployeeCardDetailModalView } from './EmployeeCardDetailModalView';
import { CardPinModal } from './CardPinModal';

export interface EmployeeCardsMenuViewProps {
  cards: EmployeeCardWithDates[];
}

export const EmployeeCardsMenuView: FC<EmployeeCardsMenuViewProps> = ({
  cards,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [detailCard, setDetailCard] = useState<EmployeeCardWithDates | null>(
    null
  );
  const [pinCard, setPinCard] = useState<EmployeeCardWithDates | null>(null);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setExpandedCardId(cards[0]?.id ?? null);
    },
    [cards]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggle = useCallback((cardId: string) => {
    setExpandedCardId((current) => (current === cardId ? null : cardId));
  }, []);

  const handleShowDetails = useCallback((card: EmployeeCardWithDates) => {
    setAnchorEl(null);
    setDetailCard(card);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailCard(null);
  }, []);

  const handleShowPin = useCallback((card: EmployeeCardWithDates) => {
    setAnchorEl(null);
    setPinCard(card);
  }, []);

  const handleClosePin = useCallback(() => {
    setPinCard(null);
  }, []);

  return (
    <>
      <Tooltip title={t('myCards.title')}>
        <MenuBarIconButton
          onClick={handleOpen}
          open={Boolean(anchorEl)}
          data-testid="employee-cards-menu-button"
        >
          <CreditCardIcon />
        </MenuBarIconButton>
      </Tooltip>
      <MenuPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{ width: 380, p: 0, mt: 0.5, ml: 0.5 }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            pt: 2,
            pb: 1.5,
          }}
        >
          <Typography variant="subtitle1" color="primary">
            {t('myCards.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('myCards.cardCount', { count: cards.length })}
          </Typography>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            px: 1.5,
            pb: 1.5,
            maxHeight: 'calc(100vh - 150px)',
            overflowY: 'auto',
          }}
        >
          {cards.map((card) => (
            <CardRow
              key={card.id}
              card={card}
              expanded={expandedCardId === card.id}
              onToggle={handleToggle}
              onShowDetails={handleShowDetails}
              onShowPin={handleShowPin}
            />
          ))}
        </Stack>
      </MenuPopover>
      {detailCard && (
        <EmployeeCardDetailModalView
          card={detailCard}
          open
          onClose={handleCloseDetails}
        />
      )}
      {pinCard && (
        <CardPinModal
          cardId={pinCard.id}
          lastFourDigits={pinCard.last_four_digits}
          onClose={handleClosePin}
        />
      )}
    </>
  );
};

interface CardRowProps {
  card: EmployeeCardWithDates;
  expanded: boolean;
  onToggle: (cardId: string) => void;
  onShowDetails: (card: EmployeeCardWithDates) => void;
  onShowPin: (card: EmployeeCardWithDates) => void;
}

const CardRow: FC<CardRowProps> = ({
  card,
  expanded,
  onToggle,
  onShowDetails,
  onShowPin,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();

  const usage = useMemo(() => getLimitUsage(card), [card]);
  const isActive = card.card_status === 'ACTIVE';
  const limitLabel = t(getContextualLimitLabel(card.limit_renew_frequency));
  const nextResetDate = useMemo(
    () =>
      isCardTerminated(card.card_status)
        ? null
        : getNextResetDate(card.limit_renew_frequency),
    [card.card_status, card.limit_renew_frequency]
  );

  return (
    <RowContainer expanded={expanded}>
      <ButtonBase
        onClick={() => onToggle(card.id)}
        sx={{ width: 1, p: 1.25, borderRadius: 1.5, textAlign: 'left' }}
        aria-expanded={expanded}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ width: 1, alignItems: 'center' }}
        >
          <CardIconBox>
            <CreditCardIcon fontSize="small" />
          </CardIconBox>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
              <Typography variant="subtitle2" noWrap>
                {t(`myCards.cardType.${card.card_type}`)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {card.last_four_digits}
              </Typography>
            </Stack>
            {!expanded &&
              (isActive ? (
                <LinearProgress
                  variant="determinate"
                  value={usage.percent}
                  color={usage.color}
                  sx={{ mt: 0.75, height: 4, borderRadius: 2 }}
                />
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <Label
                    variant="ghost"
                    color={getStatusColor(card.card_status)}
                  >
                    {t(`myCards.status.${card.card_status}`)}
                  </Label>
                </Box>
              ))}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: !isActive
                  ? 'text.secondary'
                  : card.available_balance < 0
                  ? 'error.main'
                  : 'success.main',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? fCurrencyCents(card.available_balance) : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('myCards.availableInline')}
            </Typography>
          </Box>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          />
        </Stack>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.25} sx={{ px: 1.25, pb: 1.25 }}>
          <CardFace>
            <Flourish />
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: CARD_ON_DARK_STRONG }}
              >
                {t(`myCards.cardType.${card.card_type}`)}
              </Typography>
              <Label variant="filled" color={getStatusColor(card.card_status)}>
                {t(`myCards.status.${card.card_status}`)}
              </Label>
            </Stack>
            <Chip />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'fontWeightBold',
                letterSpacing: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              •••• •••• •••• {card.last_four_digits}
            </Typography>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: CARD_ON_DARK_MUTED }}
                >
                  {t('myCards.availableBalance')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'fontWeightBold' }}>
                  {isActive ? fCurrencyCents(card.available_balance) : '—'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  sx={{ color: CARD_ON_DARK_MUTED }}
                >
                  {limitLabel}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'fontWeightBold' }}
                >
                  {fCurrencyCents(card.total_limit)}
                </Typography>
              </Box>
            </Stack>
          </CardFace>

          <Panel>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">{limitLabel}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {fCurrencyCents(getSpentAmount(card))} {t('common.of')}{' '}
                {fCurrencyCents(card.total_limit)}
              </Typography>
            </Stack>
            <UsageBar
              variant="determinate"
              value={usage.percent}
              usageColor={usage.color}
            />
          </Panel>

          <Stack direction="row" spacing={1.5}>
            <Panel sx={{ flex: 1 }}>
              <TileLabel>{t('myCards.transactionLimit')}</TileLabel>
              <TileValue>{fCurrencyCents(card.transaction_limit)}</TileValue>
            </Panel>
            <Panel sx={{ flex: 1 }}>
              <TileLabel>{t('myCards.nextReset')}</TileLabel>
              <TileValue>
                {nextResetDate
                  ? fDate(nextResetDate, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </TileValue>
            </Panel>
          </Stack>

          <Stack spacing={1}>
            {canShowCardPin(card) && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PinIcon />}
                onClick={() => onShowPin(card)}
              >
                {t('myCards.pin.show')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="inherit"
              endIcon={<ChevronRightIcon />}
              onClick={() => onShowDetails(card)}
            >
              {t('myCards.moreInformation')}
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </RowContainer>
  );
};

const RowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded, theme }) =>
  theme.unstable_sx({
    border: 1,
    borderColor: 'divider',
    borderRadius: 1.5,
    bgcolor: expanded
      ? alpha(theme.palette.primary.main, 0.04)
      : 'background.paper',
  })
);

const CardIconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

// Card-face styling mirrors creditCard in the mobile theme
// (packages/shared-mobile-components/src/theme/theme.ts). The gradient
// highlight and the chip gold have no palette equivalent.
const CARD_HIGHLIGHT = '#1E5183';
const CARD_ON_DARK_STRONG = 'rgba(255, 255, 255, 0.82)';
const CARD_ON_DARK_MUTED = 'rgba(255, 255, 255, 0.75)';

// ISO/IEC 7810 ID-1: 85.60 x 53.98 mm.
const CardFace = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    position: 'relative',
    overflow: 'hidden',
    aspectRatio: '85.6 / 53.98',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    p: 2,
    borderRadius: 4,
    color: 'common.white',
    background: `linear-gradient(135deg, ${CARD_HIGHLIGHT} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.main} 100%)`,
    boxShadow: 12,
  })
);

const Flourish = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -55,
  right: -45,
  width: 130,
  height: 130,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
  pointerEvents: 'none',
}));

const Chip = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    width: 40,
    height: 29,
    borderRadius: 1.5,
    background: 'linear-gradient(135deg, #F4DC93 0%, #D6AC48 100%)',
  })
);

const Panel = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    p: 1.5,
    borderRadius: 3,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
  })
);

const TileLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
    {children}
  </Typography>
);

const TileValue: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1">{children}</Typography>
);

const UsageBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'usageColor',
})<{ usageColor: 'success' | 'warning' | 'error' }>(
  ({ usageColor, theme }) => ({
    height: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[300],
    '& .MuiLinearProgress-bar': {
      borderRadius: theme.shape.borderRadius,
      background: `linear-gradient(90deg, ${theme.palette[usageColor].light}, ${theme.palette[usageColor].main})`,
    },
  })
);
